import { apiFetch } from '../../utils/apiClient';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as CryptoJS from 'crypto-js';
import { v4 as uuidv4 } from 'uuid';
import { UrlBuilder } from '../../services/authentication/url-builder';
import { AUTH_CONFIG } from '../../constants/authConfig';
import { retryWithBackoff } from '../../utils/apiRetry';
import logger from '../../utils/logger';
import { LOGIN_PATH } from '../../constants/uploadConfig';
// Application Config
const ApplicationConfig = {
    ClientId: process.env.REACT_APP_CLIENT_ID || '',
    IdamEndpoint: process.env.REACT_APP_IDAM_ENDPOINT,
    applicationUrl: process.env.REACT_APP_APPLICATION_URL
};

const QueryStrings = {
    Scope: 'scope',
    ResponseType: 'response_type',
    ClientId: 'client_id',
    CodeChallenge: 'code_challenge',
    CodeChallengeMethod: 'code_challenge_method',
    CodeVerifier: 'code_verifier',
    Nonce: 'nonce',
    RedirectURI: 'redirect_uri',
    GrantType: 'grant_type',
    Code: 'code',
    PostLogoutRedirectURI: 'post_logout_redirect_uri',
    IDTokenHint: 'id_token_hint',
    State: 'state',
    Logout: 'logout',
    S256: 'S256',
    T: '/t/',
    External: 'external'
};

const IDAM_ENDPOINT = {
    AUTHORIZATION: '/oauth2/authorize',
    GETALLTOKENS: '/oauth2/token',
    IDAMLOGOUTURL: '/oidc/logout',
    REVOKEIDAMTOKENURL: '/oauth2/revoke',
    INTROSPECT: '/oauth2/introspect'
};

// Helper functions
const generateRandomString = (length) => {
    let random = '';
    const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const randomValues = Array.from(crypto.getRandomValues(new Uint8Array(length)));
    randomValues.forEach(v => (random += charset[v % charset.length]));
    return random;
};

const generateCodeVerifier = () => generateRandomString(128);

const generateCodeChallenge = (codeVerifier) => {
    const base64URL = (str) => {
        return str.toString(CryptoJS.enc.Base64).replace(/=/g, '').replace(/\+/g, '-').replace(/\//g, '_');
    };
    return base64URL(CryptoJS.SHA256(codeVerifier));
};

// Async thunks
export const signIn = createAsyncThunk(
    'auth/signIn',
    async (_, { rejectWithValue }) => {
        try {
            const codeVerifier = generateCodeVerifier();
            const codeChallenge = generateCodeChallenge(codeVerifier);
            const nonce = generateRandomString(16);

            sessionStorage.setItem(QueryStrings.CodeVerifier, codeVerifier);
            sessionStorage.setItem(QueryStrings.Nonce, nonce);

            const authorizeEndpoint = `${ApplicationConfig.IdamEndpoint}${IDAM_ENDPOINT.AUTHORIZATION}`;
            const formedURL = new UrlBuilder(authorizeEndpoint)
                .withParam(QueryStrings.Scope, `openid dsire_scope ${uuidv4()}`)
                .withParam(QueryStrings.ResponseType, QueryStrings.Code)
                .withParam(QueryStrings.ClientId, ApplicationConfig.ClientId)
                .withParam(QueryStrings.CodeChallenge, codeChallenge)
                .withParam(QueryStrings.CodeChallengeMethod, QueryStrings.S256)
                .withParam(QueryStrings.Nonce, nonce)
                .withParam(QueryStrings.RedirectURI, `${ApplicationConfig.applicationUrl}/`)
                .withParam(QueryStrings.External, 'false')
                .build();

            window.location.href = formedURL;
            return true;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const completeAuthentication = createAsyncThunk(
    'auth/completeAuthentication',
    async (code, { rejectWithValue }) => {
        try {
            const codeVerifier = sessionStorage.getItem('code_verifier');
            const body = new URLSearchParams();
            body.set(QueryStrings.GrantType, 'authorization_code');
            body.set(QueryStrings.ClientId, ApplicationConfig.ClientId);
            body.set(QueryStrings.CodeVerifier, codeVerifier);
            body.set(QueryStrings.Code, code);
            body.set(QueryStrings.Scope, `openid dsire_scope ${uuidv4()}`);
            body.set(QueryStrings.RedirectURI, `${ApplicationConfig.applicationUrl}/`);

            const tokenEndpoint = `${ApplicationConfig.IdamEndpoint}${IDAM_ENDPOINT.GETALLTOKENS}`;
            const tokens = await apiFetch(tokenEndpoint, {
                method: 'POST',
                contentType: 'application/x-www-form-urlencoded',
                body: body.toString(),
                retry: true // optional, true by default
            });

            sessionStorage.setItem('bearer_token', tokens['access_token']);
            sessionStorage.setItem('refresh', tokens['refresh_token']);
            sessionStorage.setItem('id_token', tokens['id_token']);
            return tokens;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const refreshToken = createAsyncThunk(
    'auth/refreshToken',
    async (_, { rejectWithValue, dispatch }) => {
        try {
            const refreshTokenValue = sessionStorage.getItem('refresh');
            const codeVerifier = sessionStorage.getItem('code_verifier');

            if (!refreshTokenValue) {
                const error = new Error('No refresh token available');
                error.isRetryable = false;
                throw error;
            }

            const authenticationData = {
                grant_type: 'refresh_token',
                refresh_token: refreshTokenValue,
                client_id: ApplicationConfig.ClientId,
                code_verifier: codeVerifier
            };

            const body = new URLSearchParams(authenticationData).toString();
            const tokenEndpoint = `${ApplicationConfig.IdamEndpoint}${IDAM_ENDPOINT.GETALLTOKENS}`;

            const tokens = await apiFetch(tokenEndpoint, {
                method: 'POST',
                contentType: 'application/x-www-form-urlencoded',
                body,
                retry: true // optional, true by default
            });
            sessionStorage.setItem('bearer_token', tokens['access_token']);
            sessionStorage.setItem('refresh', tokens['refresh_token']);
            return tokens;
        } catch (error) {
            // After retries exhausted (except for 401/403 which already logged out)
            if (error.status !== 401 && error.status !== 403) {
                logger.error('Token refresh failed after 3 retries. Redirecting to login.');
                dispatch(signOut());
                window.location.replace(LOGIN_PATH);
            }
            return rejectWithValue(error.message);
        }
    }
);

export const signOut = createAsyncThunk(
    'auth/signOut',
    async (_, { rejectWithValue, dispatch }) => {
        try {
            const accessToken = sessionStorage.getItem('bearer_token');
            const refreshTokenValue = sessionStorage.getItem('refresh');
            const idToken = sessionStorage.getItem('id_token');
            if (!accessToken && !refreshTokenValue) {
                sessionStorage.clear();
                window.location.replace(LOGIN_PATH);
                return true;
            }
            dispatch(clearAuth());
            sessionStorage.clear();
            const tokenToRevoke = refreshTokenValue || accessToken;
            const tokenType = refreshTokenValue ? 'refresh_token' : 'access_token';
            if (tokenToRevoke) {
                try {
                    const body = new URLSearchParams();
                    body.set('token', tokenToRevoke);
                    body.set('token_type_hint', tokenType);
                    body.set(QueryStrings.ClientId, ApplicationConfig.ClientId);
                    const url = `${ApplicationConfig.IdamEndpoint}${IDAM_ENDPOINT.REVOKEIDAMTOKENURL}`;
                    await apiFetch(url, {
                        method: 'POST',
                        contentType: 'application/x-www-form-urlencoded',
                        body: body.toString(),
                        retry: false 
                    });
                } catch (error) {
                    logger.error('Error revoking token:', error);
                }
            }

           dispatch(stopPeriodicRefresh());
            const authorizeEndpoint = `${ApplicationConfig.IdamEndpoint}${IDAM_ENDPOINT.IDAMLOGOUTURL}`;
            const urlBuilder = new UrlBuilder(authorizeEndpoint)
                .withParam(QueryStrings.PostLogoutRedirectURI, `${ApplicationConfig.applicationUrl}/login`)
                .withParam(QueryStrings.State, QueryStrings.Logout);
            if (idToken) {
                urlBuilder.withParam(QueryStrings.IDTokenHint, idToken);
            }
            const formedURL = urlBuilder.build();
            window.location.replace(formedURL);
            return true;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);




export const setCurrentUser = createAsyncThunk(
    'auth/setCurrentUser',
    async ({ userDetails, aud }, { rejectWithValue }) => {
        try {
            const userObj = {
                organizationId: userDetails.claimsByAud[aud].organizationId,
                organizationName: userDetails.claimsByAud[aud].organizationName,
                roles: userDetails.claimsByAud[aud].roles,
                given_name: userDetails.claimsByAud[aud].given_name,
                family_name: userDetails.claimsByAud[aud].family_name,
                userId: userDetails.claimsByAud[aud].userId || userDetails.sub,
                email: userDetails.claimsByAud[aud].email || userDetails.sub,
                userCategory: userDetails.claimsByAud[aud].userCategory,
                userGroup: userDetails.claimsByAud[aud].organizationId || '',
            };

            sessionStorage.setItem('user-details', JSON.stringify(userObj));
            return userObj;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

// Periodic refresh token functionality
let refreshTimeoutId = null;

export const startPeriodicRefresh = () => (dispatch) => {
    // Clear any existing timeout
    if (refreshTimeoutId) {
        clearTimeout(refreshTimeoutId);
    }

    // Set up new timeout
    refreshTimeoutId = setTimeout(async () => {
        try {
            logger.debug('🔄 Starting periodic token refresh...');
            await dispatch(refreshToken());
            // Continue the cycle
            dispatch(startPeriodicRefresh());
        } catch (error) {
            console.error('❌ Periodic token refresh failed:', error);
            // Don't continue the cycle if refresh fails
        }
    }, AUTH_CONFIG.TOKEN_REFRESH_INTERVAL);
};

export const stopPeriodicRefresh = () => () => {
    if (refreshTimeoutId) {
        clearTimeout(refreshTimeoutId);
        refreshTimeoutId = null;
        logger.debug('🛑 Stopped periodic token refresh');
    }
};

// Restore authentication state from sessionStorage
export const restoreAuthState = () => (dispatch) => {
    try {
        const accessToken = sessionStorage.getItem('bearer_token');
        const refreshToken = sessionStorage.getItem('refresh');
        const userDetails = sessionStorage.getItem('user-details');

        if (accessToken && refreshToken) {
            logger.debug('🔄 Restoring authentication state from sessionStorage');

            // Restore tokens to Redux state
            dispatch(setTokens({
                access_token: accessToken,
                refresh_token: refreshToken
            }));

            // Restore user details if available
            if (userDetails) {
                try {
                    const user = JSON.parse(userDetails);
                    dispatch(setCurrentUser({ userDetails: { claimsByAud: { [user.email]: user } }, aud: user.email }));
                } catch (error) {
                    console.warn('Failed to restore user details:', error);
                }
            }

            // Start periodic refresh
            dispatch(startPeriodicRefresh());
            logger.debug('Authentication state restored successfully');
            return true;
        } else {
            logger.debug('ℹ No valid tokens found in sessionStorage');
            return false;
        }
    } catch (error) {
        console.error('Failed to restore authentication state:', error);
        return false;
    }
};

// Initial state
const initialState = {
    user: null,
    accessToken: null,
    refreshToken: null,
    isAuthenticated: false,
    isLoading: false,
    error: null,
};

// Auth slice
const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        setTokens: (state, action) => {
            state.accessToken = action.payload.access_token;
            state.refreshToken = action.payload.refresh_token;
            state.isAuthenticated = true;
        },
        clearAuth: (state) => {
            state.user = null;
            state.accessToken = null;
            state.refreshToken = null;
            state.isAuthenticated = false;
            state.error = null; // revert: clear error here
        },
    },
    extraReducers: (builder) => {
        builder
            // Sign In
            .addCase(signIn.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(signIn.fulfilled, (state) => {
                state.isLoading = false;
            })
            .addCase(signIn.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            // Complete Authentication
            .addCase(completeAuthentication.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(completeAuthentication.fulfilled, (state, action) => {
                state.isLoading = false;
                state.accessToken = action.payload.access_token;
                state.refreshToken = action.payload.refresh_token;
                state.isAuthenticated = true;
            })
            .addCase(completeAuthentication.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            // Refresh Token
            .addCase(refreshToken.pending, (state) => {
                state.error = null;
            })
            .addCase(refreshToken.fulfilled, (state, action) => {
                state.accessToken = action.payload.access_token;
                state.refreshToken = action.payload.refresh_token;
                state.isAuthenticated = true;
            })
            .addCase(refreshToken.rejected, (state, action) => {
                state.error = action.payload;
                // Optionally, trigger notification here if not handled in UI
                // Example: dispatch(showNotification({ message: action.payload, type: 'error' }));
            })
            // Sign Out
            .addCase(signOut.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(signOut.fulfilled, (state) => {
                state.user = null;
                state.accessToken = null;
                state.refreshToken = null;
                state.isAuthenticated = false;
                state.isLoading = false;
            })
            .addCase(signOut.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            // Set Current User
            .addCase(setCurrentUser.fulfilled, (state, action) => {
                state.user = action.payload;
            })
            .addCase(setCurrentUser.rejected, (state, action) => {
                state.error = action.payload;
            });
    },
});

export const { clearError, setTokens, clearAuth } = authSlice.actions;
export default authSlice.reducer;



